import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { API_BASE } from "@/App";
import { Shield, Clock, CheckCircle, XCircle, AlertTriangle, ArrowRight, Timer } from "lucide-react";

/**
 * CSAT Diagnostic Test
 * Short 15-25 minute test to assess CSAT safety
 * Based on CLARIAS SUPREME SPEC Step 5
 */

const CSATDiagnosticPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(20 * 60); // 20 minutes
  const [testStarted, setTestStarted] = useState(false);
  const [testComplete, setTestComplete] = useState(false);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Auth guard - redirect if logged out during test
  useEffect(() => {
    if (!user || !token) {
      navigate('/auth', { replace: true });
    }
  }, [user, token, navigate]);

  // Fetch diagnostic questions
  useEffect(() => {
    if (user && token) {
      fetchQuestions();
    }
  }, [user, token]);

  // Timer
  useEffect(() => {
    if (!testStarted || testComplete) return;
    
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [testStarted, testComplete]);

  const fetchQuestions = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await managedAxios.get(`${API_BASE}/command/diagnostic/csat`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestions(response.data.questions || []);
    } catch (error) {
      console.error("Error fetching questions:", error);
      // Use fallback questions
      setQuestions(generateFallbackQuestions());
    } finally {
      setLoading(false);
    }
  };

  const generateFallbackQuestions = () => {
    // 20 CSAT-style questions for diagnostic
    return [
      {
        id: 1,
        question: "A train 150m long passes a pole in 15 seconds. What is its speed in km/hr?",
        options: { A: "36 km/hr", B: "40 km/hr", C: "45 km/hr", D: "50 km/hr" },
        correct: "A",
        risk_level: "safe"
      },
      {
        id: 2,
        question: "If 30% of a number is 45, what is 50% of that number?",
        options: { A: "65", B: "75", C: "85", D: "90" },
        correct: "B",
        risk_level: "safe"
      },
      {
        id: 3,
        question: "In a certain code, COMPUTER is written as RFUVQNPC. How will MEDICINE be written?",
        options: { A: "MFEJDJOF", B: "ENICIDME", C: "FNDJDJOF", D: "GOJEJEOF" },
        correct: "C",
        risk_level: "medium"
      },
      {
        id: 4,
        question: "A and B can do a work in 12 days, B and C in 15 days, C and A in 20 days. In how many days can A alone do the work?",
        options: { A: "30 days", B: "40 days", C: "60 days", D: "120 days" },
        correct: "A",
        risk_level: "risky"
      },
      {
        id: 5,
        question: "Statement: All roses are flowers. Some flowers are red.\nConclusion I: Some roses are red.\nConclusion II: Some red things are roses.",
        options: { A: "Only I follows", B: "Only II follows", C: "Both follow", D: "Neither follows" },
        correct: "D",
        risk_level: "medium"
      },
      {
        id: 6,
        question: "The ratio of ages of A and B is 3:5. After 6 years, the ratio becomes 2:3. What is B's present age?",
        options: { A: "30 years", B: "25 years", C: "20 years", D: "15 years" },
        correct: "A",
        risk_level: "safe"
      },
      {
        id: 7,
        question: "In a row of 40 students, R is 11th from the left and S is 29th from the right. How many students are between R and S?",
        options: { A: "0", B: "1", C: "2", D: "Cannot be determined" },
        correct: "A",
        risk_level: "safe"
      },
      {
        id: 8,
        question: "A shopkeeper marks his goods 25% above cost price and allows 10% discount. His profit percentage is:",
        options: { A: "12.5%", B: "15%", C: "17.5%", D: "10%" },
        correct: "A",
        risk_level: "medium"
      },
      {
        id: 9,
        question: "If FRIEND is coded as HUMJTK, how will CANDLE be coded?",
        options: { A: "EDRIRL", B: "DCPFMG", C: "ESJGNK", D: "ECRIRL" },
        correct: "A",
        risk_level: "safe"
      },
      {
        id: 10,
        question: "The average of 5 consecutive numbers is 18. The largest number is:",
        options: { A: "20", B: "22", C: "24", D: "26" },
        correct: "A",
        risk_level: "safe"
      },
      {
        id: 11,
        question: "A sum of money doubles itself in 8 years at simple interest. The rate of interest is:",
        options: { A: "10%", B: "12.5%", C: "15%", D: "20%" },
        correct: "B",
        risk_level: "safe"
      },
      {
        id: 12,
        question: "Statement: Should there be a complete ban on strikes?\nArgument I: Yes, it hampers productivity.\nArgument II: No, it is a democratic right.",
        options: { A: "Only I is strong", B: "Only II is strong", C: "Both are strong", D: "Neither is strong" },
        correct: "C",
        risk_level: "medium"
      },
      {
        id: 13,
        question: "Find the missing number: 2, 6, 12, 20, 30, ?",
        options: { A: "40", B: "42", C: "44", D: "46" },
        correct: "B",
        risk_level: "safe"
      },
      {
        id: 14,
        question: "A boat travels 36 km downstream in 4 hours and 48 km upstream in 6 hours. The speed of the boat in still water is:",
        options: { A: "8 km/hr", B: "9 km/hr", C: "10 km/hr", D: "8.5 km/hr" },
        correct: "D",
        risk_level: "medium"
      },
      {
        id: 15,
        question: "If South-East becomes North, then North-East will become:",
        options: { A: "South", B: "South-West", C: "North-West", D: "West" },
        correct: "B",
        risk_level: "safe"
      },
      {
        id: 16,
        question: "The HCF and LCM of two numbers are 12 and 336 respectively. If one number is 84, find the other.",
        options: { A: "48", B: "36", C: "60", D: "72" },
        correct: "A",
        risk_level: "safe"
      },
      {
        id: 17,
        question: "In how many ways can 5 books be arranged on a shelf?",
        options: { A: "60", B: "120", C: "240", D: "720" },
        correct: "B",
        risk_level: "safe"
      },
      {
        id: 18,
        question: "Statement: All politicians are honest. No honest person is corrupt.\nConclusion I: No politician is corrupt.\nConclusion II: Some corrupt persons are politicians.",
        options: { A: "Only I follows", B: "Only II follows", C: "Both follow", D: "Neither follows" },
        correct: "A",
        risk_level: "risky"
      },
      {
        id: 19,
        question: "A car covers 450 km in 5 hours. What is the average speed if the car covers the last 150 km at 60 km/hr?",
        options: { A: "90 km/hr", B: "100 km/hr", C: "85 km/hr", D: "Cannot be determined" },
        correct: "D",
        risk_level: "risky"
      },
      {
        id: 20,
        question: "Three dice are thrown together. Find the probability of getting a sum of 10.",
        options: { A: "1/8", B: "27/216", C: "1/4", D: "5/36" },
        correct: "A",
        risk_level: "risky"
      }
    ];
  };

  const handleAnswer = (optionKey) => {
    setAnswers({ ...answers, [questions[currentIndex].id]: optionKey });
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    
    // Calculate score
    let correct = 0;
    let wrong = 0;
    let skipped = 0;
    let safeCorrect = 0;
    let safeWrong = 0;
    let riskyAttempted = 0;
    
    questions.forEach((q) => {
      const userAnswer = answers[q.id];
      if (!userAnswer) {
        skipped++;
      } else if (userAnswer === q.correct) {
        correct++;
        if (q.risk_level === "safe") safeCorrect++;
      } else {
        wrong++;
        if (q.risk_level === "safe") safeWrong++;
        if (q.risk_level === "risky") riskyAttempted++;
      }
    });
    
    // Calculate CSAT safety score (0-100)
    const totalAttempted = correct + wrong;
    const accuracy = totalAttempted > 0 ? (correct / totalAttempted) * 100 : 0;
    const safetyScore = Math.round(accuracy);
    
    // Determine status
    let status, reason, instruction;
    if (safetyScore >= 70 && safeWrong <= 2) {
      status = "safe";
      reason = "Your CSAT foundation is solid. Safe question accuracy is high.";
      instruction = "Maintain consistency. Practice 10 questions daily.";
    } else if (safetyScore >= 50) {
      status = "borderline";
      reason = `You missed ${safeWrong} safe questions. This is risky in actual exam.`;
      instruction = "Focus on safe question types. Avoid risky calculations.";
    } else {
      status = "unsafe";
      reason = "Your accuracy on safe questions is below passing threshold.";
      instruction = "Daily CSAT practice is mandatory. Start with basic concepts.";
    }
    
    const resultData = {
      score: safetyScore,
      correct,
      wrong,
      skipped,
      total: questions.length,
      status,
      reason,
      instruction,
      safe_accuracy: safeCorrect + safeWrong > 0 ? Math.round((safeCorrect / (safeCorrect + safeWrong)) * 100) : 0,
      risky_attempted: riskyAttempted
    };
    
    setResult(resultData);
    setTestComplete(true);
    
    // Submit to backend - complete action AND record behavior for analytics
    try {
      const token = localStorage.getItem("token");
      
      // 1. Complete the forced action
      await managedAxios.post(
        `${API_BASE}/command/complete-action`,
        null,
        {
          params: { action_type: "csat_diagnostic", score: safetyScore },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // 2. Record behavior for Core Intelligence analytics
      const totalTimeUsed = 20 * 60 - timeLeft; // seconds used
      await managedAxios.post(
        `${API_BASE}/intelligence/record-behavior`,
        {
          test_id: `csat_diagnostic_${Date.now()}`,
          test_type: "csat_diagnostic",
          total_questions: questions.length,
          attempted: correct + wrong,
          correct: correct,
          wrong: wrong,
          unattempted: skipped,
          score: safetyScore,
          accuracy: accuracy,
          total_time_seconds: totalTimeUsed,
          avg_time_per_question: totalTimeUsed / (correct + wrong || 1),
          high_confidence_correct: safeCorrect,
          high_confidence_wrong: safeWrong,
          elimination_usage_rate: safeCorrect / (safeCorrect + safeWrong || 1) * 100
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // 3. Navigate to feedback page with results
      navigate("/diagnostic-feedback", {
        state: {
          testType: "csat",
          score: safetyScore,
          status: status,
          correct: correct,
          total: questions.length,
          timeSpent: totalTimeUsed,
          safeAccuracy: resultData.safe_accuracy
        }
      });
      
    } catch (error) {
      console.error("Error submitting result:", error);
    }
    
    setSubmitting(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "safe": return "text-emerald-500";
      case "borderline": return "text-amber-500";
      case "unsafe": return "text-red-500";
      default: return "text-slate-500";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "safe": return <CheckCircle className="w-16 h-16 text-emerald-500" />;
      case "borderline": return <AlertTriangle className="w-16 h-16 text-amber-500" />;
      case "unsafe": return <XCircle className="w-16 h-16 text-red-500" />;
      default: return null;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "safe": return "🟢 SAFE";
      case "borderline": return "🟡 BORDERLINE";
      case "unsafe": return "❌ UNSAFE";
      default: return "";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Pre-test screen
  if (!testStarted && !testComplete) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full border-0 bg-slate-800/50 backdrop-blur-xl">
          <CardContent className="p-8 text-center">
            <Shield className="w-16 h-16 text-blue-400 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-white mb-2">CSAT Safety Check</h1>
            <p className="text-slate-400 mb-6">
              This diagnostic will assess your CSAT safety status.
            </p>
            
            <div className="bg-slate-700/50 rounded-lg p-4 mb-6 text-left">
              <div className="flex items-center gap-2 text-slate-300 mb-2">
                <Clock className="w-4 h-4" />
                <span>20 minutes</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300 mb-2">
                <CheckCircle className="w-4 h-4" />
                <span>20 questions</span>
              </div>
              <p className="text-sm text-slate-400 mt-3">
                Focus on <strong>safe questions</strong>. Accuracy matters more than speed.
              </p>
            </div>
            
            <Button
              onClick={() => setTestStarted(true)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-lg py-6"
            >
              Start Diagnostic
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Result screen (POST-ACTION FEEDBACK - Step 6)
  if (testComplete && result) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full border-0 bg-slate-800/50 backdrop-blur-xl">
          <CardContent className="p-8 text-center">
            {getStatusIcon(result.status)}
            
            <h1 className={`text-3xl font-bold mt-4 ${getStatusColor(result.status)}`}>
              {getStatusBadge(result.status)}
            </h1>
            
            <p className="text-slate-300 mt-4 text-lg">
              {result.reason}
            </p>
            
            <p className="text-slate-400 mt-2">
              {result.instruction}
            </p>
            
            {/* Score summary */}
            <div className="grid grid-cols-3 gap-4 mt-6">
              <div className="bg-slate-700/50 rounded-lg p-3">
                <p className="text-2xl font-bold text-emerald-400">{result.correct}</p>
                <p className="text-xs text-slate-400">Correct</p>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-3">
                <p className="text-2xl font-bold text-red-400">{result.wrong}</p>
                <p className="text-xs text-slate-400">Wrong</p>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-3">
                <p className="text-2xl font-bold text-slate-400">{result.skipped}</p>
                <p className="text-xs text-slate-400">Skipped</p>
              </div>
            </div>
            
            {/* Safety metrics */}
            <div className="bg-slate-700/30 rounded-lg p-4 mt-4">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Safe Question Accuracy</span>
                <span className="text-white font-medium">{result.safe_accuracy}%</span>
              </div>
            </div>
            
            <Button
              onClick={() => navigate("/dashboard")}
              className="w-full mt-6 bg-blue-600 hover:bg-blue-700 py-6"
            >
              Continue to Dashboard
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Test in progress
  const currentQuestion = questions[currentIndex];
  const answeredCount = Object.keys(answers).length;

  return (
    <div className="min-h-screen bg-slate-900 p-4">
      {/* Header */}
      <div className="max-w-3xl mx-auto mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-400" />
            <span className="text-white font-medium">CSAT Safety Check</span>
          </div>
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
            timeLeft < 300 ? "bg-red-500/20 text-red-400" : "bg-slate-700 text-slate-300"
          }`}>
            <Timer className="w-4 h-4" />
            <span className="font-mono">{formatTime(timeLeft)}</span>
          </div>
        </div>
        
        {/* Progress */}
        <div className="flex gap-1 mt-4">
          {questions.map((_, idx) => (
            <div
              key={idx}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                answers[questions[idx]?.id]
                  ? "bg-emerald-500"
                  : idx === currentIndex
                  ? "bg-blue-500"
                  : "bg-slate-700"
              }`}
            />
          ))}
        </div>
        <p className="text-slate-500 text-sm mt-2">
          {answeredCount} of {questions.length} answered
        </p>
      </div>

      {/* Question Card */}
      <Card className="max-w-3xl mx-auto border-0 bg-slate-800/50 backdrop-blur-xl">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-slate-500">Question {currentIndex + 1}</span>
            {currentQuestion.risk_level && (
              <span className={`text-xs px-2 py-1 rounded-full ${
                currentQuestion.risk_level === "safe" ? "bg-emerald-500/20 text-emerald-400" :
                currentQuestion.risk_level === "medium" ? "bg-amber-500/20 text-amber-400" :
                "bg-red-500/20 text-red-400"
              }`}>
                {currentQuestion.risk_level.toUpperCase()}
              </span>
            )}
          </div>
          
          <p className="text-white text-lg mb-6 whitespace-pre-wrap">
            {currentQuestion.question}
          </p>
          
          {/* Options */}
          <div className="space-y-3">
            {Object.entries(currentQuestion.options).map(([key, value]) => (
              <button
                key={key}
                onClick={() => handleAnswer(key)}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                  answers[currentQuestion.id] === key
                    ? "border-blue-500 bg-blue-500/10 text-white"
                    : "border-slate-700 hover:border-slate-600 text-slate-300 hover:bg-slate-700/50"
                }`}
              >
                <span className="font-medium mr-3">{key}.</span>
                {value}
              </button>
            ))}
          </div>
          
          {/* Navigation */}
          <div className="flex justify-between mt-6">
            <Button
              variant="outline"
              onClick={handlePrev}
              disabled={currentIndex === 0}
              className="border-slate-600"
            >
              Previous
            </Button>
            
            {currentIndex < questions.length - 1 ? (
              <Button onClick={handleNext}>
                Next
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {submitting ? "Submitting..." : "Submit Test"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CSATDiagnosticPage;
